import { Span } from "./token.js";

export type { Span } from "./token.js";

// ---------------------------------------------------------------------------
// Type expressions (as written in source, e.g. `float`, `Set<Player>`)
// ---------------------------------------------------------------------------

export interface NamedType {
  kind: "named";
  name: string;
  args: TypeExpr[]; // e.g. Set<Player>
  span: Span;
}

/** A first-class function type, as written `(T, U) -> R`. Parameters are
 *  positional (like a `fn` declaration); `ret` is the result type (`void` when
 *  the function returns nothing). This is what lets a function be passed as a
 *  value — annotate a parameter `f: (int) -> int` and call it as `f(x)`. */
export interface FnType {
  kind: "fn";
  params: TypeExpr[];
  ret: TypeExpr;
  span: Span;
}

export type TypeExpr = NamedType | FnType;

/**
 * Built-in generic container type names — identifiers that may take
 * angle-bracketed type arguments (`Set<T>`, `Dict<K, V>`) and be built with the
 * generic-constructor syntax. Kept here — a dependency-free module — so the parser
 * and the type checker agree on the set without importing each other. Adding a new
 * container (e.g. `List`) starts by listing it here. (`Future<T>` is *not* here: it
 * is an ordinary generic struct declared in `concurrency.stm`, not a native
 * container.)
 */
export const GENERIC_CONTAINERS = ["Set", "Dict", "List"] as const;

export function isGenericContainer(name: string): boolean {
  return (GENERIC_CONTAINERS as readonly string[]).includes(name);
}

// ---------------------------------------------------------------------------
// Top-level declarations
// ---------------------------------------------------------------------------

export interface FieldDecl {
  name: string;
  nameSpan: Span;
  type: TypeExpr;
  /** `true` if the field carries the `key` modifier: every field being `key` makes
   *  the struct a value type (see `isValueType`). Absent on ordinary fields. */
  key?: boolean;
  /** Default value (`x int = expr`): the expression a construction uses when the
   *  field is omitted. Evaluated *afresh at each construction* (so a `List` default
   *  is a distinct list every time), not once at declaration. Must be a closed
   *  expression (it may reference globals, but not other fields). */
  default?: Expr;
  span: Span;
}

export interface StructDecl {
  kind: "struct";
  name: string;
  nameSpan: Span;
  /** Generic type parameters, e.g. `A`, `B` in `struct Pair<A, B>`. A field may
   *  mention them; a construction / annotation supplies concrete arguments.
   *  Empty for a plain (non-generic) struct. */
  typeParams: TypeParam[];
  fields: FieldDecl[];
  /** `true` for a `builtin struct` — a natively-backed type declared (headers only,
   *  no fields) in the stdlib so its name has a source location for go-to-definition
   *  and hover: the built-in collections `Set`/`Dict`/`List`. The checker
   *  records it as a type but routes its values/construction through the container
   *  machinery; the backends ignore it (they provide the collection natively). */
  builtin?: boolean;
  span: Span;
}

/**
 * A struct is a **value type** when *every* field is marked `key` — including the
 * degenerate case of no fields at all (`Unit`), which has no mutable state and so
 * is trivially a value. Value types are immutable (mutating a field is a type
 * error), stored inline rather than heap-allocated (they never get their own KV
 * cell in the store), and used *by value*: as a set element or dict key their
 * fields are hashed, instead of keying on object identity. Their fields are
 * restricted to scalars and other value types so an inline, hashable
 * representation always exists — the checker enforces that. This is the one
 * definition the parser, checker, interpreter, and Go backend all share.
 *
 * A generic struct is never a value type: its fields' types are type parameters,
 * which have no fixed inline, hashable layout — so it is always heap-allocated.
 */
export function isValueType(s: StructDecl): boolean {
  // A `builtin struct` (fieldless, natively-backed) is never a value type — its
  // vacuous "every field is `key`" must not classify it inline/hashable.
  return !s.builtin && s.typeParams.length === 0 && s.fields.every((f) => f.key === true);
}

export interface EnumVariant {
  name: string;
  nameSpan: Span;
  fields: FieldDecl[]; // may be empty
  span: Span;
}

export interface EnumDecl {
  kind: "enum";
  name: string;
  nameSpan: Span;
  typeParams: TypeParam[];
  variants: EnumVariant[];
  span: Span;
}

export interface Param {
  name: string;
  nameSpan: Span;
  type: TypeExpr;
  /** Default value (`y: int = expr`): the expression a call uses when the argument is
   *  omitted. Evaluated *afresh at each call* (a `List` default is a distinct list
   *  every time), not once at declaration. Must be a closed expression (globals only,
   *  not other parameters). A defaulted parameter may not precede a required one. */
  default?: Expr;
  span: Span;
}

/** A generic function's type parameter with optional trait bounds, e.g. the
 *  `T: Ord + Show` in `fn f<T: Ord + Show>(...)`. Bounds are trait names the
 *  argument type must implement; inside the body a value of type `T` can call
 *  those traits' methods. */
export interface TypeParam {
  name: string;
  nameSpan: Span;
  bounds: { name: string; span: Span }[];
  span: Span;
}

/** An effect a function/method may perform, declared with a trailing
 *  `! io retry raises spawn` annotation after the signature. A closed vocabulary —
 *  no user-defined effects (see docs/design/effect-syntax.md). */
export type EffectName = "io" | "retry" | "raises" | "spawn";

export interface FnDecl {
  kind: "fn";
  name: string;
  nameSpan: Span;
  /** Generic type parameters, e.g. `<T: Countable>`. Empty for a plain function
   *  and for trait/impl methods (which aren't generic themselves). */
  typeParams: TypeParam[];
  /** Span of the `self` receiver, present iff this is a trait/impl method. `self`
   *  is not included in `params`; its type is the impl's target type. */
  self?: Span;
  params: Param[];
  returnType: TypeExpr | null;
  /** `true` when declared with the `builtin` keyword (`builtin fn print(value)`):
   *  the function is *declared* in source but *implemented* natively — the
   *  interpreter and Go backend plug an implementation in by name, so there is no
   *  body to run. `body` is an empty block. The standard library is written this
   *  way (see `stdlib.ts`). */
  builtin?: boolean;
  /** Declared effects, from a trailing `! io retry raises spawn` annotation after the
   *  signature (`builtin fn print(value) ! io`). Absent/empty = pure. For a
   *  `builtin` this *declares* the effect set (the checker cannot see a native
   *  body); for an ordinary function it is a checked upper bound — its effects are
   *  otherwise *inferred* from what it calls. The checker rejects IO inside a `txn`,
   *  requires an enclosing `txn` for `retry`, and forbids an uncaught `raise` from
   *  escaping one (see `checker.ts`). */
  effects?: EffectName[];
  body: Block;
  span: Span;
}

/** One method inside a `trait` declaration. A missing body makes the method
 *  required; a body supplies the default used by impls that omit the method.
 *  Every trait method takes a `self` receiver (`self` names the implementing
 *  type); `params` are the arguments after it. */
export interface TraitMethodSig {
  name: string;
  nameSpan: Span;
  /** The method's own generic type parameters, e.g. `U` in `map<U>(self, ...)`.
   *  Distinct from the trait's type parameters; inferred at each call site. Empty
   *  for a non-generic method. */
  typeParams: TypeParam[];
  self: Span;
  params: Param[];
  returnType: TypeExpr | null;
  /** Declared effects, from a trailing `! io retry raises spawn` annotation (`fn
   *  read(self) T ! io`). Absent/empty = pure; see `FnDecl.effects`. */
  effects?: EffectName[];
  body?: Block;
  span: Span;
}

export interface TraitDecl {
  kind: "trait";
  name: string;
  nameSpan: Span;
  /** Generic type parameters, e.g. `T` in `trait Iterator<T>`. A method signature
   *  may mention them; an impl supplies concrete arguments for them. Empty for a
   *  plain (non-generic) trait. */
  typeParams: TypeParam[];
  methods: TraitMethodSig[];
  span: Span;
}

/** `impl [<T, ...>] Trait for Target { ...methods }` — implements a trait's
 *  methods for a struct, value type, enum, or a generic container. `methods` are
 *  ordinary `FnDecl`s carrying a `self` receiver.
 *
 *  A generic impl (`impl<T> Trait for Set<T>`) binds type parameters that the
 *  target may reference; the impl then applies to every instantiation of that
 *  container (`Set<int>`, `Set<Player>`, …). `typeName` is the target's head name
 *  (`Set`), the key methods are registered under. */
export interface ImplDecl {
  kind: "impl";
  /** The target's head name, so `decls`-level lookups (e.g. builtin shadowing)
   *  have a `name` like every other decl. */
  name: string;
  /** Type parameters bound by the impl, e.g. `["T"]` in `impl<T> … for Set<T>`. */
  typeParams: string[];
  /** The implemented trait's name, or `""` for an *inherent* impl (`impl Target
   *  { … }`) — methods attached directly to a type with no trait. */
  trait: string;
  /** The trait's type arguments, e.g. `int` in `impl Iterator<int> for Range`.
   *  Empty for a non-generic trait. */
  traitArgs: TypeExpr[];
  traitSpan: Span;
  /** The full target type expression, e.g. `Set<T>` or `Circle`. Always a named
   *  type — a function type is not a valid `impl` target. */
  target: NamedType;
  /** The target's head name (`Set`, `Circle`). */
  typeName: string;
  /** Span of just the target's head name (for go-to-definition). */
  typeSpan: Span;
  methods: FnDecl[];
  span: Span;
}

export type Decl = StructDecl | EnumDecl | FnDecl | TraitDecl | ImplDecl;

export interface Program {
  decls: Decl[];
  span: Span;
}

/** Whether `program` *declares* `name` itself — a struct, enum, trait, or fn of
 *  its own — and so shadows the prelude declaration of that name. Every stage that
 *  assembles the prelude (checker, interpreter, monomorphizer) asks this before
 *  including a built-in declaration.
 *
 *  An `impl` is deliberately not such a declaration: its `name` is the type it
 *  attaches methods to, so `impl<T> Option<T> { … }` — extending a prelude type,
 *  which is allowed — must not be read as *replacing* the prelude's `Option`. */
export function shadowsPrelude(program: Program, name: string): boolean {
  return program.decls.some((d) => d.kind !== "impl" && d.name === name);
}

// ---------------------------------------------------------------------------
// Statements
// ---------------------------------------------------------------------------

export interface Block {
  kind: "block";
  stmts: Stmt[];
  span: Span;
}

/** `name = expr` where `name` is not yet bound -> declaration. */
export interface LetStmt {
  kind: "let";
  name: string;
  nameSpan: Span;
  type: TypeExpr | null;
  value: Expr;
  span: Span;
}

/** Assignment to an lvalue: `x = e`, `p.pos = e`, `p.pos += e`. */
export interface AssignStmt {
  kind: "assign";
  target: Expr; // Ident or FieldAccess
  op: "=" | "+=" | "-=" | "*=" | "/=";
  value: Expr;
  span: Span;
}

export interface ExprStmt {
  kind: "exprStmt";
  expr: Expr;
  span: Span;
}

export interface ReturnStmt {
  kind: "return";
  value: Expr | null;
  span: Span;
}

export interface IfStmt {
  kind: "if";
  cond: Expr;
  then: Block;
  else: Block | IfStmt | null;
  span: Span;
}

export interface WhileStmt {
  kind: "while";
  cond: Expr;
  body: Block;
  span: Span;
}

export interface ForStmt {
  kind: "for";
  varName: string;
  varSpan: Span;
  iterable: Expr;
  body: Block;
  span: Span;
}

/** `go f(...)`: spawn a thread running `f`, fire-and-forget (the result is
 *  discarded). To capture a spawned thread's result, use the stdlib `spawn`, which
 *  wraps a `go` in a `Future<T>`.
 *
 *  A `go` is the seed of the `spawn` effect: it makes the enclosing callable
 *  spawning, and a spawning callable may not be called inside a `txn` (a
 *  transaction may abort and re-run, which would spawn the thread again). */
export interface GoStmt {
  kind: "go";
  call: CallExpr;
  span: Span;
}

export interface TxnStmt {
  kind: "txn";
  body: Block;
  span: Span;
}

/** `retry`: inside a `txn`, abandon the current attempt and block until a cell the
 *  transaction read is changed by a committed write, then re-run from the top. The
 *  STM blocking primitive — see `docs/design/concurrency-via-retry.md`. */
export interface RetryStmt {
  kind: "retry";
  span: Span;
}

/** `raise e` / `raise e from cause`: abandon the current computation with the
 *  error value `e` (anything implementing the built-in `Error` trait). The value
 *  travels out of band — past every intermediate frame — until a `catch` clause
 *  matching it runs, or it reaches the top of a thread and aborts the program with
 *  its message and stack trace.
 *
 *  The optional `from cause` clause records *another* error as what led to this
 *  one, so a low-level failure survives being wrapped in a domain error (see
 *  docs/design/exceptions.md#chaining-raise--from). The stack trace, by contrast,
 *  is implicit: the runtime captures it at the raise site, never the programmer. */
export interface RaiseStmt {
  kind: "raise";
  value: Expr;
  cause: Expr | null;
  span: Span;
}

/** The `catch` clause of a `try`. It takes no type — it catches **every** error and
 *  binds it to `name` as the built-in `Error` trait. Handling one error type
 *  differently from another is done *inside* the handler by dispatching on the bound
 *  value's type, and re-raising what you don't want (see `RaiseStmt`); a typed
 *  `catch e: T` clause would be sugar for exactly that, and is deliberately not a
 *  second mechanism (docs/design/type-unions.md). */
export interface CatchClause {
  name: string;
  nameSpan: Span;
  body: Block;
  span: Span;
}

/** `try { … } catch e { … }`: run `body`; if it raises, bind the error and run the
 *  handler. A `try` takes exactly one `catch`.
 *
 *  `catch` is the `raises` effect's *discharge* point: because it catches
 *  everything, a `try`/`catch` stops the effect propagating to the enclosing
 *  function (see `checker.ts`) — unless the handler re-raises. */
export interface TryStmt {
  kind: "try";
  body: Block;
  handler: CatchClause;
  span: Span;
}

export interface MatchBinding {
  field: string; // variant field being pulled out
  fieldSpan: Span;
  name: string; // local name it's bound to (same as `field` unless renamed)
  nameSpan: Span; // span of the local name (the `field` span when not renamed)
  renamed: boolean; // true when written as `field: name`
}

export interface MatchArm {
  variant: string; // the variant name, or `"_"` for a wildcard arm
  variantSpan: Span;
  bindings: MatchBinding[];
  body: Block;
  span: Span;
  wildcard: boolean; // `_ => ...` — a catch-all arm that matches any variant
}

export interface MatchStmt {
  kind: "match";
  subject: Expr;
  arms: MatchArm[];
  span: Span;
}

/** `assert cond` / `assert cond, "message"`: check a condition at runtime and
 *  record the verdict, in the spirit of Python's `assert`. The condition must be
 *  a `bool` and the optional message a `string` (its label).
 *
 *  Unlike Python's, a failing assertion is *soft*: it emits an `assert` trace
 *  event carrying its pass/fail and label, then execution continues. That is what
 *  lets the race detector treat an assertion as a first-class *outcome* of a run —
 *  the timeline-tree search looks for forks where the same assertion passes down
 *  one branch and fails down another (see `playground/timeline.ts`). Aborting the
 *  thread (like a `raise`) is a mode we may add later; soft is the default. */
export interface AssertStmt {
  kind: "assert";
  cond: Expr;
  message: Expr | null;
  span: Span;
}

export type Stmt =
  | LetStmt
  | AssignStmt
  | ExprStmt
  | ReturnStmt
  | IfStmt
  | WhileStmt
  | ForStmt
  | GoStmt
  | TxnStmt
  | RetryStmt
  | RaiseStmt
  | TryStmt
  | MatchStmt
  | AssertStmt
  | Block;

// ---------------------------------------------------------------------------
// Expressions
// ---------------------------------------------------------------------------

export interface IntLit {
  kind: "int";
  value: number;
  span: Span;
}

export interface FloatLit {
  kind: "float";
  value: number;
  span: Span;
}

export interface StringLit {
  kind: "string";
  value: string;
  span: Span;
}

/** A character literal, e.g. `'a'` or `'\n'`. `value` is its Unicode code point
 *  (a number), the same representation a `char` value carries at runtime. */
export interface CharLit {
  kind: "char";
  value: number;
  span: Span;
}

export interface BoolLit {
  kind: "bool";
  value: boolean;
  span: Span;
}

export interface Ident {
  kind: "ident";
  name: string;
  span: Span;
}

export interface BinaryExpr {
  kind: "binary";
  op: "+" | "-" | "*" | "/" | "%" | "==" | "!=" | "<" | ">" | "<=" | ">=" | "&&" | "||";
  left: Expr;
  right: Expr;
  span: Span;
}

export interface UnaryExpr {
  kind: "unary";
  op: "-" | "!";
  operand: Expr;
  span: Span;
}

export interface FieldAccess {
  kind: "field";
  object: Expr;
  field: string;
  /** Span of just the field name (for hovers / go-to-definition). */
  fieldSpan: Span;
  span: Span;
}

export interface CallArg {
  name: string | null; // named argument, e.g. Point(x: 1.0)
  /** Span of the argument name, when named — the go-to-definition source for a field. */
  nameSpan?: Span;
  value: Expr;
}

export interface CallExpr {
  kind: "call";
  callee: Expr; // Ident (fn / constructor) or FieldAccess (method / enum variant)
  args: CallArg[];
  /** Explicit type arguments, e.g. the `Point` in `Set<Point>()`. Usually empty. */
  typeArgs: TypeExpr[];
  span: Span;
}

/** One parameter of a lambda. Its type is optional: when omitted it is inferred
 *  as `unknown` (best-effort — the checker's leniency lets a bare-parameter
 *  lambda still flow into a `(int) -> int` slot). */
export interface LambdaParam {
  name: string;
  nameSpan: Span;
  type: TypeExpr | null;
  span: Span;
}

/** An anonymous function that captures its enclosing scope: `(x) -> x + n`
 *  closes over `n`. The body is either an *expression* (its value is what the
 *  lambda returns) or a *block* `(x) -> { ... return e }` (which returns via
 *  `return`, or `void` if it never does). A lambda's type is the function type
 *  `(params) -> bodyType`. */
export interface LambdaExpr {
  kind: "lambda";
  params: LambdaParam[];
  body: Expr | Block;
  span: Span;
}

/** An `if`/`else` used as an *expression*: `if cond { a } else { b }`, whose value
 *  is the taken branch's. Distinct from the `if` *statement* ([`IfStmt`](#IfStmt)) in
 *  three ways: the `else` is mandatory (there must be a value when the condition is
 *  false), each branch is a single expression in braces rather than a statement
 *  block, and the checker requires the two branch types to agree (with an `int`
 *  widening to `float` as in a list literal). An `else if` chain nests — the `else`
 *  is another `IfExpr`.
 *
 *  The `if` keyword heads both a statement and this expression; there is no
 *  ambiguity because a statement-position `if` is always parsed as `IfStmt` (see
 *  `parseStmt`), so an `IfExpr` only arises where an expression is expected — an
 *  argument, a `let`/assignment right-hand side, an operand. Rust makes the branch a
 *  full block; keeping it a single expression is the one departure, and it is what
 *  lets an if-expression stay a pure expression in a language with a strict
 *  statement/expression split (a `{ … }` statement block never appears in expression
 *  position). */
export interface IfExpr {
  kind: "ifExpr";
  cond: Expr;
  /** The value when `cond` is true — the expression inside the `then` braces. */
  then: Expr;
  /** The value when `cond` is false — the expression inside the `else` braces, or a
   *  nested `IfExpr` for an `else if` chain. Always present (an if-expression's
   *  `else` is mandatory). */
  else: Expr;
  span: Span;
}

/** A list literal, `[a, b, c]` — a fresh `List<T>` holding the elements in order.
 *  Unlike every other literal this one *constructs* (it allocates a collection and
 *  writes an entry per element), so two evaluations of the same literal are two
 *  distinct lists. The element type is not written: the checker infers it from the
 *  elements, or from the expected type when the context supplies one — which is
 *  also the only thing that can type an empty `[]`. See
 *  [the design note](../../docs/design/collection-literals.md). */
export interface ListLit {
  kind: "list";
  elements: Expr[];
  span: Span;
}

/** One entry of a brace literal: a dict's `key: value` pair, or a set's element
 *  (which carries no `key`). A dict key is an *expression*, so `{name: 1}` keys on
 *  the variable `name` and `{"name": 1}` on the string — Python's rule, not
 *  JavaScript's (this language stringifies nothing implicitly). Note it reads like a
 *  named argument (`f(name: 1)`) but is not one: that spelling means a parameter
 *  name inside `(…)` and a key expression inside `{…}`. */
export interface BraceEntry {
  key?: Expr;
  value: Expr;
}

/** A brace literal — `{k: v, …}` for a `Dict<K, V>`, `{a, b, …}` for a `Set<T>`.
 *  One node because they are one syntactic form: the parser tells them apart by
 *  whether a `:` follows the first element, and an empty `{}` is *neither* until the
 *  checker reads its expected type. Constructs on every evaluation and infers its
 *  types, exactly like a [`ListLit`](#ListLit); a dict's later duplicate keys
 *  overwrite earlier ones, and a set's duplicate elements collapse.
 *
 *  A `{` also opens a block, so a brace literal is not allowed to appear directly in
 *  the header of an `if`/`while`/`for`/`match` (nor as a statement, nor as a lambda
 *  body) — parenthesize it there. The parser enforces that; see `noBraceLiteral`. */
export interface BraceLit {
  kind: "brace";
  /** `"Dict"` when the entries carry keys, `"Set"` when they don't — and `null` for
   *  `{}`, whose container only the context can supply (an error without one). */
  container: "Set" | "Dict" | null;
  entries: BraceEntry[];
  span: Span;
}

export type Expr =
  | IntLit
  | FloatLit
  | StringLit
  | CharLit
  | BoolLit
  | ListLit
  | BraceLit
  | Ident
  | BinaryExpr
  | UnaryExpr
  | FieldAccess
  | CallExpr
  | LambdaExpr
  | IfExpr;
